/**
 * MobileBottomNav Component
 *
 * Apple Music liquid glass inspired bottom navigation bar.
 *
 * Features:
 * - Floating glass pill with frosted backdrop blur for main navigation items
 * - Separate circular glass button for chat
 * - Active state with inner pill highlight and brand-colored icon + label
 * - All nav items show icon + label (active item highlighted with primary color)
 * - Safe area inset support for modern devices (iPhone X+)
 *
 * @see {MobileTopBar} - Top bar with logo, coin balance, and profile
 * @see {Sidebar} - Desktop navigation component
 * @see {MainLayout} - Parent layout component
 */

import {
	Home,
	Play,
	Search,
	type LucideIcon,
	MessageCircle,
	Podcast,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/contexts/auth.context";
import { useState, useEffect } from "react";
import { NavigationService } from "@/lib/auth/navigation.service";
import type { NavLink } from "@/components/ui/navbar";
import { GlobalChat } from "@/features/chat/components/globalChat.component";
import { usePlayerStore } from "@/lib/player/player.store";
import { RegisterPromptDialog } from "@/components/registerPromptDialog.component";

// Map route paths to icons
const getIconForPath = (path: string): LucideIcon => {
	if (path.includes("/dashboard") || path === "/") return Home;
	if (path.includes("/explore")) return Search;
	if (path.includes("/podspace")) return Podcast;
	if (path.includes("/podcast")) return Play;
	return Home; // default fallback
};

interface MobileNavItem {
	icon: LucideIcon;
	label: string;
	path: string;
	isActive: boolean;
}

export function MobileBottomNav() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation("common");
	const { isAuthenticated } = useAuth();
	const [navLinks, setNavLinks] = useState<NavLink[]>([]);
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
	const podWorkspaceId = usePlayerStore((state) => state.podWorkspaceId);
	const currentMedia = usePlayerStore((state) => state.currentMedia);

	// Fetch navigation links
	useEffect(() => {
		const fetchLinks = async () => {
			try {
				const links = await NavigationService.getNavLinks();
				setNavLinks(links);
			} catch (err) {
				console.error("Failed to fetch navigation links", err);
				setNavLinks([]);
			}
		};
		fetchLinks();
	}, []);

	// Build navigation items from fetched links (take first 4 for mobile)
	const navItems: MobileNavItem[] = navLinks.slice(0, 4).map((link) => ({
		icon: getIconForPath(link.href),
		label: t(link.label),
		path: link.href,
		isActive:
			location.pathname === link.href ||
			(link.href !== "/dashboard" && location.pathname.startsWith(link.href)),
	}));

	const handleNavClick = (path?: string) => {
		if (!isAuthenticated) {
			navigate("/auth/login");
			return;
		}
		if (path) {
			navigate(path);
		}
	};

	const handleChatClick = () => {
		if (!isAuthenticated) {
			setShowRegisterPrompt(true);
			return;
		}
		setIsChatOpen(true);
	};

	return (
		<>
			{/* Bottom Navigation Bar — Floating glass pill + circle layout */}
			<nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)]">
				<div className="flex items-center gap-2 mx-3 mb-2">
					{/* ── Glass Navigation Pill ── */}
					<div className="flex-1 flex items-center justify-around h-[58px] rounded-[28px] liquid-glass px-1">
						{navItems.map((item) => {
							const isActive = isAuthenticated && item.isActive;
							return (
								<button
									key={item.path || item.label}
									onClick={() => handleNavClick(item.path)}
									className="relative flex flex-col items-center justify-center gap-0.5 h-[44px] flex-1 transition-all duration-200 active:scale-90"
									type="button"
									aria-label={item.label}
								>
									{/* Active inner pill highlight */}
									{isActive && (
										<div className="absolute inset-x-1 inset-y-0 liquid-glass-surface rounded-2xl" />
									)}
									<item.icon
										className={`relative z-10 h-6 w-6 transition-colors duration-200 ${
											isActive ? "text-primary" : "text-muted-foreground"
										}`}
										strokeWidth={isActive ? 2.5 : 1.8}
									/>
									<span
										className={`relative z-10 text-[10px] leading-tight transition-colors duration-200 max-w-[64px] truncate ${
											isActive
												? "text-primary font-semibold"
												: "text-muted-foreground font-medium"
										}`}
									>
										{item.label}
									</span>
								</button>
							);
						})}
					</div>

					{/* ── Chat Circle Button ── */}
					<button
						onClick={handleChatClick}
						className="flex-shrink-0 h-[58px] w-[58px] rounded-full liquid-glass flex items-center justify-center transition-all duration-200 active:scale-90"
						type="button"
						aria-label={t("chat.openChat")}
					>
						<MessageCircle
							className={`h-6 w-6 transition-colors duration-200 ${
								isChatOpen ? "text-primary" : "text-muted-foreground"
							}`}
							strokeWidth={isChatOpen ? 2.5 : 1.8}
						/>
					</button>
				</div>
			</nav>

			{/* Register Prompt Dialog for Chat */}
			<RegisterPromptDialog
				open={showRegisterPrompt}
				onOpenChange={setShowRegisterPrompt}
				title={t("chat.registerPrompt.title")}
				description={t("chat.registerPrompt.description")}
				customMessage={t("chat.registerPrompt.message")}
			/>

			{/* Global Chat */}
			{isChatOpen && (
				<GlobalChat
					defaultOpen={isChatOpen}
					onOpenChange={setIsChatOpen}
					podWorkspaceId={podWorkspaceId || undefined}
					workspaceTitle={currentMedia?.subtitle}
					hideTrigger={true}
				/>
			)}
		</>
	);
}
